import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { User, LogOut, UserPen, ExternalLink, Settings as SettingsIcon } from 'lucide-react';

/**
 * User Profile Component
 * Displays user information and provides logout functionality
 */
const UserProfile = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/login');
  };

  const handleEditProfile = () => {
    setShowDropdown(false);
    if (setActiveTab) {
      setActiveTab(8); // Index for Profile edit section
    }
  };

  const handleSettings = () => {
    setShowDropdown(false);
    if (setActiveTab) {
      setActiveTab(7); // Index for Settings section
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="user-profile dropdown">
      {/* Profile Button */}
      <button
        className="btn btn-sm d-flex align-items-center gap-2 user-profile-btn"
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label={`Open user menu for ${user.username}`}
        aria-expanded={showDropdown}
        aria-haspopup="menu"
      >
        <User size={18} />
        <span>{user.username}</span>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="dropdown-menu show" style={{ display: 'block' }}>
          {/* User Info */}
          <div className="dropdown-header">
            <div className="fw-bold">{user.firstName} {user.lastName}</div>
            <div className="text-muted small">{user.email}</div>
          </div>

          <hr className="dropdown-divider" />

          {/* Profile Edit Link */}
          <button
            className="dropdown-item"
            onClick={handleEditProfile}
          >
            <UserPen size={16} className="me-2" />
            {t('profile')}
          </button>

          {/* Settings Link (Admin Only) */}
          {isAdmin() && (
            <>
              <button
                className="dropdown-item"
                onClick={handleSettings}
              >
                <SettingsIcon size={16} className="me-2" />
                {t('settingsLabel')}
              </button>
            </>
          )}

          <hr className="dropdown-divider" />

          {/* External Links */}
          <a
            className="dropdown-item"
            href="https://freqtrade.carbonem.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} className="me-2" />
            Freqtrade
          </a>

          <a
            className="dropdown-item"
            href="https://deepseek.carbonem.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} className="me-2" />
            DeepSeek
          </a>

          <hr className="dropdown-divider" />

          {/* Logout Link */}
          <button
            className="dropdown-item text-danger"
            onClick={handleLogout}
          >
            <LogOut size={16} className="me-2" />
            {t('logout')}
          </button>
        </div>
      )}

      {/* Inline Dropdown CSS */}
      <style>{`
        .user-profile {
          position: relative;
        }

        .user-profile-btn {
          background-color: rgba(255, 255, 255, 0.15);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .user-profile-btn:hover {
          background-color: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.5);
          color: white;
        }

        .user-profile-btn:active,
        .user-profile-btn:focus {
          background-color: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
          color: white;
          box-shadow: 0 0 0 0.25rem rgba(255, 255, 255, 0.25);
        }

        .user-profile .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          min-width: 250px;
          margin-top: 5px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          background-color: white;
          border: 1px solid #dee2e6;
        }

        .user-profile .dropdown-header {
          padding: 10px 16px;
          background-color: #f8f9fa;
        }

        .user-profile .dropdown-divider {
          margin: 0.5rem 0;
        }

        .user-profile .dropdown-item {
          padding: 10px 16px;
          border: none;
          background: none;
          text-align: left;
          width: 100%;
          cursor: pointer;
          font-size: 14px;
          display: flex;
          align-items: center;
          color: #212529;
          transition: background-color 0.2s ease;
        }

        .user-profile .dropdown-item:hover {
          background-color: #f5f5f5;
          color: #212529;
          text-decoration: none;
        }

        .user-profile .dropdown-item.text-danger:hover {
          background-color: #fee;
          color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default UserProfile;
